import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ImageUpload, IMAGE_FORMATS } from '../components/ui/ImageUpload';
import { ChevronRight, Info, Globe, MapPin, Phone, Calendar, Users, Clock, Sparkles, Heart, MessageSquare, Award, Leaf, Wrench } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Spanish postal code lookup (simplified version)
const lookupSpanishPostalCode = (postalCode) => {
  const postalCodes = {
    '38001': 'Santa Cruz de Tenerife',
    '38002': 'Santa Cruz de Tenerife',
    '38003': 'Santa Cruz de Tenerife',
    '38400': 'Puerto de la Cruz',
    '38650': 'Los Cristianos',
    '38660': 'Costa Adeje',
    '35001': 'Las Palmas de Gran Canaria',
    '35002': 'Las Palmas de Gran Canaria',
    '35100': 'Maspalomas',
    '28001': 'Madrid',
    '08001': 'Barcelona',
  };
  
  const city = postalCodes[postalCode];
  return { found: !!city, city: city || '' };
};

// Service Types (matching mobile app)
const SERVICE_TYPES = [
  { key: 'tantric_massage', label: { es: 'Masaje Tántrico', en: 'Tantric Massage' }, icon: '✋' },
  { key: 'tantra_coaching', label: { es: 'Coaching Tántrico', en: 'Tantra Coaching' }, icon: '💬' },
  { key: 'couple_session', label: { es: 'Sesión de Pareja', en: 'Couple Session' }, icon: '❤️' },
  { key: 'tantra_therapy', label: { es: 'Terapia Tántrica', en: 'Tantra Therapy' }, icon: '🏥' },
  { key: 'tantra_initiation', label: { es: 'Iniciación Tántrica', en: 'Tantra Initiation' }, icon: '✨' },
];

// Course Types (matching mobile app)
const COURSE_TYPES = [
  { key: 'workshop', label: { es: 'Taller', en: 'Workshop' }, icon: Wrench, description: { es: 'Sesión intensiva corta', en: 'Short intensive session' } },
  { key: 'retreat', label: { es: 'Retiro', en: 'Retreat' }, icon: Leaf, description: { es: 'Experiencia inmersiva de varios días', en: 'Multi-day immersive experience' } },
];

// Target Audience options
const TARGET_AUDIENCE = [
  { key: 'all', label: { es: 'Todos', en: 'Everyone' } },
  { key: 'women', label: { es: 'Mujeres', en: 'Women' } },
  { key: 'men', label: { es: 'Hombres', en: 'Men' } },
  { key: 'couples', label: { es: 'Parejas', en: 'Couples' } },
  { key: 'lgbtqia', label: { es: 'LGBTQIA+', en: 'LGBTQIA+' } },
];

// Comprehensive Spanish postal codes database with coordinates
// Canary Islands (Tenerife, Gran Canaria, etc.)
const SPANISH_POSTAL_CODES = {
  // TENERIFE (38xxx)
  '38001': { city: 'Santa Cruz de Tenerife', lat: 28.4636, lng: -16.2518 },
  '38002': { city: 'Santa Cruz de Tenerife', lat: 28.4682, lng: -16.2546 },
  '38003': { city: 'Santa Cruz de Tenerife', lat: 28.4598, lng: -16.2489 },
  '38004': { city: 'Santa Cruz de Tenerife', lat: 28.4571, lng: -16.2612 },
  '38005': { city: 'Santa Cruz de Tenerife', lat: 28.4523, lng: -16.2687 },
  '38006': { city: 'Santa Cruz de Tenerife', lat: 28.4489, lng: -16.2734 },
  '38007': { city: 'Santa Cruz de Tenerife', lat: 28.4612, lng: -16.2789 },
  '38008': { city: 'Santa Cruz de Tenerife', lat: 28.4534, lng: -16.2856 },
  '38009': { city: 'Santa Cruz de Tenerife', lat: 28.4478, lng: -16.2912 },
  '38010': { city: 'Santa Cruz de Tenerife', lat: 28.4389, lng: -16.2978 },
  '38100': { city: 'Santa Cruz de Tenerife', lat: 28.4636, lng: -16.2518 },
  '38107': { city: 'Santa Cruz de Tenerife', lat: 28.4701, lng: -16.2467 },
  '38108': { city: 'Santa Cruz de Tenerife', lat: 28.4756, lng: -16.2389 },
  '38109': { city: 'Santa Cruz de Tenerife', lat: 28.4823, lng: -16.2312 },
  '38111': { city: 'Santa Cruz de Tenerife', lat: 28.4567, lng: -16.2645 },
  '38120': { city: 'San Cristóbal de La Laguna', lat: 28.4853, lng: -16.3156 },
  '38200': { city: 'San Cristóbal de La Laguna', lat: 28.4853, lng: -16.3156 },
  '38201': { city: 'San Cristóbal de La Laguna', lat: 28.4889, lng: -16.3189 },
  '38202': { city: 'San Cristóbal de La Laguna', lat: 28.4912, lng: -16.3234 },
  '38203': { city: 'San Cristóbal de La Laguna', lat: 28.4945, lng: -16.3278 },
  '38204': { city: 'San Cristóbal de La Laguna', lat: 28.4978, lng: -16.3312 },
  '38205': { city: 'San Cristóbal de La Laguna', lat: 28.5012, lng: -16.3356 },
  '38206': { city: 'Tegueste', lat: 28.5167, lng: -16.3333 },
  '38207': { city: 'San Cristóbal de La Laguna', lat: 28.5089, lng: -16.3423 },
  '38208': { city: 'San Cristóbal de La Laguna', lat: 28.5123, lng: -16.3489 },
  '38250': { city: 'Bajamar', lat: 28.5456, lng: -16.3567 },
  '38260': { city: 'Tejina', lat: 28.5234, lng: -16.3789 },
  '38270': { city: 'Valle Guerra', lat: 28.5123, lng: -16.3923 },
  '38280': { city: 'Tegueste', lat: 28.5167, lng: -16.3333 },
  '38290': { city: 'Las Mercedes', lat: 28.5312, lng: -16.2978 },
  '38300': { city: 'La Orotava', lat: 28.3906, lng: -16.5231 },
  '38310': { city: 'La Orotava', lat: 28.3856, lng: -16.5178 },
  '38311': { city: 'La Orotava', lat: 28.3923, lng: -16.5289 },
  '38312': { city: 'La Orotava', lat: 28.3978, lng: -16.5345 },
  '38313': { city: 'La Orotava', lat: 28.4012, lng: -16.5412 },
  '38314': { city: 'La Orotava', lat: 28.4067, lng: -16.5478 },
  '38315': { city: 'La Orotava', lat: 28.4123, lng: -16.5534 },
  '38320': { city: 'San Juan de la Rambla', lat: 28.3889, lng: -16.6167 },
  '38330': { city: 'La Guancha', lat: 28.3667, lng: -16.6500 },
  '38340': { city: 'Tacoronte', lat: 28.4756, lng: -16.4123 },
  '38350': { city: 'Tacoronte', lat: 28.4801, lng: -16.4189 },
  '38355': { city: 'El Sauzal', lat: 28.4678, lng: -16.4356 },
  '38358': { city: 'Tacoronte', lat: 28.4834, lng: -16.4234 },
  '38359': { city: 'El Sauzal', lat: 28.4623, lng: -16.4412 },
  '38360': { city: 'El Sauzal', lat: 28.4567, lng: -16.4478 },
  '38370': { city: 'La Matanza de Acentejo', lat: 28.4456, lng: -16.4623 },
  '38379': { city: 'La Matanza de Acentejo', lat: 28.4512, lng: -16.4567 },
  '38380': { city: 'La Victoria de Acentejo', lat: 28.4345, lng: -16.4789 },
  '38390': { city: 'Santa Úrsula', lat: 28.4234, lng: -16.4923 },
  '38400': { city: 'Puerto de la Cruz', lat: 28.4147, lng: -16.5479 },
  '38410': { city: 'Los Realejos', lat: 28.3923, lng: -16.5867 },
  '38411': { city: 'Los Realejos', lat: 28.3978, lng: -16.5912 },
  '38412': { city: 'Los Realejos', lat: 28.4012, lng: -16.5978 },
  '38413': { city: 'Los Realejos', lat: 28.4067, lng: -16.6034 },
  '38414': { city: 'Los Realejos', lat: 28.4123, lng: -16.6089 },
  '38415': { city: 'Los Realejos', lat: 28.4178, lng: -16.6145 },
  '38416': { city: 'Los Realejos', lat: 28.4234, lng: -16.6201 },
  '38417': { city: 'Los Realejos', lat: 28.4289, lng: -16.6256 },
  '38418': { city: 'Los Realejos', lat: 28.4345, lng: -16.6312 },
  '38419': { city: 'Los Realejos', lat: 28.4401, lng: -16.6367 },
  '38430': { city: 'Icod de los Vinos', lat: 28.3667, lng: -16.7167 },
  '38434': { city: 'Icod de los Vinos', lat: 28.3712, lng: -16.7223 },
  '38435': { city: 'Icod de los Vinos', lat: 28.3756, lng: -16.7289 },
  '38436': { city: 'Icod de los Vinos', lat: 28.3801, lng: -16.7345 },
  '38437': { city: 'Icod de los Vinos', lat: 28.3845, lng: -16.7401 },
  '38438': { city: 'Icod de los Vinos', lat: 28.3889, lng: -16.7456 },
  '38439': { city: 'Icod de los Vinos', lat: 28.3934, lng: -16.7512 },
  '38440': { city: 'La Guancha', lat: 28.3667, lng: -16.6500 },
  '38450': { city: 'Garachico', lat: 28.3717, lng: -16.7617 },
  '38460': { city: 'Buenavista del Norte', lat: 28.3750, lng: -16.8583 },
  '38470': { city: 'Los Silos', lat: 28.3733, lng: -16.8167 },
  '38480': { city: 'Buenavista del Norte', lat: 28.3789, lng: -16.8634 },
  '38489': { city: 'El Palmar', lat: 28.3567, lng: -16.8289 },
  '38500': { city: 'Güímar', lat: 28.3167, lng: -16.4167 },
  '38508': { city: 'Güímar', lat: 28.3212, lng: -16.4223 },
  '38509': { city: 'Güímar', lat: 28.3256, lng: -16.4289 },
  '38530': { city: 'Candelaria', lat: 28.3542, lng: -16.3725 },
  '38540': { city: 'Candelaria', lat: 28.3589, lng: -16.3789 },
  '38550': { city: 'Arafo', lat: 28.3456, lng: -16.4123 },
  '38560': { city: 'Güímar', lat: 28.3301, lng: -16.4345 },
  '38570': { city: 'Fasnia', lat: 28.2333, lng: -16.4333 },
  '38580': { city: 'Arico', lat: 28.1667, lng: -16.4833 },
  '38588': { city: 'Arico', lat: 28.1712, lng: -16.4889 },
  '38589': { city: 'Arico', lat: 28.1756, lng: -16.4934 },
  '38590': { city: 'Granadilla de Abona', lat: 28.1167, lng: -16.5833 },
  '38591': { city: 'Granadilla de Abona', lat: 28.1212, lng: -16.5889 },
  '38592': { city: 'Granadilla de Abona', lat: 28.1256, lng: -16.5934 },
  '38593': { city: 'San Isidro', lat: 28.0667, lng: -16.5500 },
  '38594': { city: 'Granadilla de Abona', lat: 28.1301, lng: -16.5978 },
  '38595': { city: 'Granadilla de Abona', lat: 28.1345, lng: -16.6023 },
  '38600': { city: 'Granadilla de Abona', lat: 28.1167, lng: -16.5833 },
  '38610': { city: 'Granadilla de Abona', lat: 28.1212, lng: -16.5889 },
  '38611': { city: 'Granadilla de Abona', lat: 28.1256, lng: -16.5934 },
  '38612': { city: 'El Médano', lat: 28.0445, lng: -16.5364 },
  '38613': { city: 'El Médano', lat: 28.0489, lng: -16.5412 },
  '38614': { city: 'Granadilla de Abona', lat: 28.1301, lng: -16.5978 },
  '38615': { city: 'Granadilla de Abona', lat: 28.1345, lng: -16.6023 },
  '38616': { city: 'Granadilla de Abona', lat: 28.1389, lng: -16.6067 },
  '38617': { city: 'Granadilla de Abona', lat: 28.1434, lng: -16.6112 },
  '38618': { city: 'Granadilla de Abona', lat: 28.1478, lng: -16.6156 },
  '38620': { city: 'San Miguel de Abona', lat: 28.0967, lng: -16.6167 },
  '38626': { city: 'San Miguel de Abona', lat: 28.1012, lng: -16.6212 },
  '38627': { city: 'Aldea Blanca', lat: 28.0534, lng: -16.6089 },
  '38628': { city: 'San Miguel de Abona', lat: 28.1056, lng: -16.6256 },
  '38629': { city: 'San Miguel de Abona', lat: 28.1101, lng: -16.6301 },
  '38630': { city: 'Costa del Silencio', lat: 28.0067, lng: -16.6456 },
  '38631': { city: 'Las Galletas', lat: 28.0089, lng: -16.6589 },
  '38632': { city: 'Palm-Mar', lat: 28.0123, lng: -16.6712 },
  '38639': { city: 'Golf del Sur', lat: 28.0345, lng: -16.6234 },
  '38640': { city: 'Arona', lat: 28.0997, lng: -16.6808 },
  '38648': { city: 'Arona', lat: 28.1034, lng: -16.6856 },
  '38649': { city: 'Arona', lat: 28.1078, lng: -16.6901 },
  '38650': { city: 'Los Cristianos', lat: 28.0519, lng: -16.7152 },
  '38651': { city: 'Arona', lat: 28.1123, lng: -16.6945 },
  '38652': { city: 'Playa de las Américas', lat: 28.0567, lng: -16.7234 },
  '38660': { city: 'Costa Adeje', lat: 28.0821, lng: -16.7266 },
  '38670': { city: 'Adeje', lat: 28.1235, lng: -16.7260 },
  '38677': { city: 'Adeje', lat: 28.1278, lng: -16.7312 },
  '38678': { city: 'Adeje', lat: 28.1323, lng: -16.7367 },
  '38679': { city: 'Callao Salvaje', lat: 28.1345, lng: -16.7812 },
  '38680': { city: 'Guía de Isora', lat: 28.2167, lng: -16.7833 },
  '38683': { city: 'Santiago del Teide', lat: 28.2956, lng: -16.8156 },
  '38685': { city: 'Playa San Juan', lat: 28.1789, lng: -16.8123 },
  '38686': { city: 'Alcalá', lat: 28.1923, lng: -16.8234 },
  '38687': { city: 'Guía de Isora', lat: 28.2212, lng: -16.7889 },
  '38688': { city: 'Guía de Isora', lat: 28.2256, lng: -16.7934 },
  '38689': { city: 'San Juan', lat: 28.2301, lng: -16.7978 },
  '38690': { city: 'Santiago del Teide', lat: 28.2956, lng: -16.8156 },
  '38700': { city: 'Santa Cruz de La Palma', lat: 28.6833, lng: -17.7647 },
  '38710': { city: 'Breña Alta', lat: 28.6500, lng: -17.7833 },
  '38712': { city: 'Breña Baja', lat: 28.6333, lng: -17.7667 },
  '38713': { city: 'Breña Baja', lat: 28.6367, lng: -17.7712 },
  '38714': { city: 'Puntallana', lat: 28.7333, lng: -17.7667 },
  '38715': { city: 'Puntallana', lat: 28.7378, lng: -17.7712 },
  '38720': { city: 'San Andrés y Sauces', lat: 28.8000, lng: -17.7833 },
  '38726': { city: 'Barlovento', lat: 28.8333, lng: -17.8167 },
  '38727': { city: 'Barlovento', lat: 28.8378, lng: -17.8212 },
  '38728': { city: 'San Andrés y Sauces', lat: 28.8045, lng: -17.7889 },
  '38729': { city: 'San Andrés y Sauces', lat: 28.8089, lng: -17.7934 },
  '38730': { city: 'Villa de Mazo', lat: 28.6000, lng: -17.7833 },
  '38738': { city: 'Villa de Mazo', lat: 28.6045, lng: -17.7889 },
  '38739': { city: 'Villa de Mazo', lat: 28.6089, lng: -17.7934 },
  '38740': { city: 'Fuencaliente', lat: 28.4833, lng: -17.8500 },
  '38749': { city: 'Fuencaliente', lat: 28.4878, lng: -17.8545 },
  '38750': { city: 'El Paso', lat: 28.6500, lng: -17.8833 },
  '38758': { city: 'El Paso', lat: 28.6545, lng: -17.8878 },
  '38759': { city: 'El Paso', lat: 28.6589, lng: -17.8923 },
  '38760': { city: 'Los Llanos de Aridane', lat: 28.6583, lng: -17.9167 },
  '38767': { city: 'Los Llanos de Aridane', lat: 28.6623, lng: -17.9212 },
  '38768': { city: 'Los Llanos de Aridane', lat: 28.6667, lng: -17.9256 },
  '38769': { city: 'Los Llanos de Aridane', lat: 28.6712, lng: -17.9301 },
  '38780': { city: 'Tijarafe', lat: 28.7000, lng: -17.9500 },
  '38787': { city: 'Garafía', lat: 28.8167, lng: -17.9167 },
  '38788': { city: 'Puntagorda', lat: 28.7667, lng: -17.9833 },
  '38789': { city: 'Tijarafe', lat: 28.7045, lng: -17.9545 },
  
  // GRAN CANARIA (35xxx)
  '35001': { city: 'Las Palmas de Gran Canaria', lat: 28.1235, lng: -15.4366 },
  '35002': { city: 'Las Palmas de Gran Canaria', lat: 28.1278, lng: -15.4312 },
  '35003': { city: 'Las Palmas de Gran Canaria', lat: 28.1323, lng: -15.4256 },
  '35004': { city: 'Las Palmas de Gran Canaria', lat: 28.1367, lng: -15.4201 },
  '35005': { city: 'Las Palmas de Gran Canaria', lat: 28.1412, lng: -15.4145 },
  '35006': { city: 'Las Palmas de Gran Canaria', lat: 28.1456, lng: -15.4089 },
  '35007': { city: 'Las Palmas de Gran Canaria', lat: 28.1501, lng: -15.4034 },
  '35008': { city: 'Las Palmas de Gran Canaria', lat: 28.1545, lng: -15.3978 },
  '35009': { city: 'Las Palmas de Gran Canaria', lat: 28.1589, lng: -15.3923 },
  '35010': { city: 'Las Palmas de Gran Canaria', lat: 28.1634, lng: -15.3867 },
  '35011': { city: 'Las Palmas de Gran Canaria', lat: 28.1678, lng: -15.3812 },
  '35012': { city: 'Las Palmas de Gran Canaria', lat: 28.1723, lng: -15.3756 },
  '35013': { city: 'Las Palmas de Gran Canaria', lat: 28.1767, lng: -15.3701 },
  '35014': { city: 'Las Palmas de Gran Canaria', lat: 28.1812, lng: -15.3645 },
  '35015': { city: 'Las Palmas de Gran Canaria', lat: 28.1856, lng: -15.3589 },
  '35016': { city: 'Las Palmas de Gran Canaria', lat: 28.1901, lng: -15.3534 },
  '35017': { city: 'Las Palmas de Gran Canaria', lat: 28.1045, lng: -15.4201 },
  '35018': { city: 'Las Palmas de Gran Canaria', lat: 28.1089, lng: -15.4145 },
  '35019': { city: 'Las Palmas de Gran Canaria', lat: 28.1134, lng: -15.4089 },
  '35100': { city: 'Maspalomas', lat: 27.7606, lng: -15.5863 },
  '35106': { city: 'San Bartolomé de Tirajana', lat: 27.9234, lng: -15.5723 },
  '35107': { city: 'San Fernando', lat: 27.7701, lng: -15.5934 },
  '35108': { city: 'Playa del Inglés', lat: 27.7556, lng: -15.5745 },
  '35109': { city: 'San Agustín', lat: 27.7712, lng: -15.5489 },
  '35110': { city: 'Santa Lucía de Tirajana', lat: 27.9117, lng: -15.5400 },
  '35118': { city: 'Agüimes', lat: 27.9056, lng: -15.4456 },
  '35119': { city: 'Agüimes', lat: 27.9101, lng: -15.4501 },
  '35120': { city: 'Arguineguín', lat: 27.7600, lng: -15.6800 },
  '35128': { city: 'Mogán', lat: 27.8867, lng: -15.7233 },
  '35129': { city: 'Puerto Rico', lat: 27.7889, lng: -15.7089 },
  '35130': { city: 'Puerto de Mogán', lat: 27.8167, lng: -15.7667 },
  '35138': { city: 'Mogán', lat: 27.8912, lng: -15.7278 },
  '35139': { city: 'Mogán', lat: 27.8956, lng: -15.7323 },
  '35200': { city: 'Telde', lat: 27.9946, lng: -15.4167 },
  '35210': { city: 'Telde', lat: 27.9989, lng: -15.4212 },
  '35211': { city: 'Telde', lat: 28.0034, lng: -15.4256 },
  '35212': { city: 'Telde', lat: 28.0078, lng: -15.4301 },
  '35213': { city: 'Telde', lat: 28.0123, lng: -15.4345 },
  '35214': { city: 'Telde', lat: 28.0167, lng: -15.4389 },
  '35215': { city: 'Telde', lat: 28.0212, lng: -15.4434 },
  '35216': { city: 'Telde', lat: 28.0256, lng: -15.4478 },
  '35217': { city: 'Valsequillo', lat: 27.9833, lng: -15.4833 },
  '35218': { city: 'Telde', lat: 28.0301, lng: -15.4523 },
  '35219': { city: 'Telde', lat: 28.0345, lng: -15.4567 },
  '35220': { city: 'Telde', lat: 28.0389, lng: -15.4612 },
  '35229': { city: 'Valsequillo', lat: 27.9878, lng: -15.4878 },
  '35230': { city: 'Valsequillo', lat: 27.9833, lng: -15.4833 },
  '35240': { city: 'Carrizal', lat: 27.9789, lng: -15.4545 },
  '35250': { city: 'Ingenio', lat: 27.9167, lng: -15.4333 },
  '35259': { city: 'Ingenio', lat: 27.9212, lng: -15.4378 },
  '35260': { city: 'Agüimes', lat: 27.9056, lng: -15.4456 },
  '35270': { city: 'Agüimes', lat: 27.9101, lng: -15.4501 },
  '35280': { city: 'Cruce de Arinaga', lat: 27.8723, lng: -15.4134 },
  '35290': { city: 'Arinaga', lat: 27.8589, lng: -15.3978 },
  '35300': { city: 'Santa Brígida', lat: 28.0333, lng: -15.5000 },
  '35307': { city: 'Santa Brígida', lat: 28.0378, lng: -15.5045 },
  '35308': { city: 'Santa Brígida', lat: 28.0423, lng: -15.5089 },
  '35309': { city: 'Santa Brígida', lat: 28.0467, lng: -15.5134 },
  '35310': { city: 'Santa Brígida', lat: 28.0512, lng: -15.5178 },
  '35320': { city: 'Vega de San Mateo', lat: 28.0167, lng: -15.5333 },
  '35328': { city: 'Vega de San Mateo', lat: 28.0212, lng: -15.5378 },
  '35329': { city: 'Tejeda', lat: 27.9833, lng: -15.6500 },
  '35330': { city: 'Tejeda', lat: 27.9833, lng: -15.6500 },
  '35338': { city: 'Tejeda', lat: 27.9878, lng: -15.6545 },
  '35339': { city: 'Tejeda', lat: 27.9923, lng: -15.6589 },
  '35340': { city: 'Valleseco', lat: 28.0500, lng: -15.5833 },
  '35350': { city: 'Teror', lat: 28.0597, lng: -15.5469 },
  '35360': { city: 'Teror', lat: 28.0642, lng: -15.5514 },
  '35369': { city: 'Teror', lat: 28.0686, lng: -15.5558 },
  '35400': { city: 'Arucas', lat: 28.1197, lng: -15.5231 },
  '35411': { city: 'Arucas', lat: 28.1242, lng: -15.5276 },
  '35412': { city: 'Arucas', lat: 28.1286, lng: -15.5320 },
  '35413': { city: 'Arucas', lat: 28.1331, lng: -15.5365 },
  '35414': { city: 'Arucas', lat: 28.1375, lng: -15.5409 },
  '35415': { city: 'Arucas', lat: 28.1420, lng: -15.5454 },
  '35416': { city: 'Arucas', lat: 28.1464, lng: -15.5498 },
  '35417': { city: 'Arucas', lat: 28.1509, lng: -15.5543 },
  '35418': { city: 'Moya', lat: 28.1117, lng: -15.5858 },
  '35419': { city: 'Moya', lat: 28.1161, lng: -15.5903 },
  '35420': { city: 'Moya', lat: 28.1117, lng: -15.5858 },
  '35421': { city: 'Moya', lat: 28.1161, lng: -15.5903 },
  '35422': { city: 'Firgas', lat: 28.1133, lng: -15.5583 },
  '35423': { city: 'Firgas', lat: 28.1178, lng: -15.5628 },
  '35430': { city: 'Firgas', lat: 28.1133, lng: -15.5583 },
  '35431': { city: 'Firgas', lat: 28.1178, lng: -15.5628 },
  '35432': { city: 'Firgas', lat: 28.1223, lng: -15.5673 },
  '35450': { city: 'Santa María de Guía', lat: 28.1361, lng: -15.6333 },
  '35458': { city: 'Santa María de Guía', lat: 28.1406, lng: -15.6378 },
  '35459': { city: 'Santa María de Guía', lat: 28.1450, lng: -15.6423 },
  '35460': { city: 'Gáldar', lat: 28.1475, lng: -15.6506 },
  '35468': { city: 'Gáldar', lat: 28.1520, lng: -15.6551 },
  '35469': { city: 'Gáldar', lat: 28.1564, lng: -15.6595 },
  '35470': { city: 'La Aldea de San Nicolás', lat: 27.9833, lng: -15.7833 },
  '35478': { city: 'La Aldea de San Nicolás', lat: 27.9878, lng: -15.7878 },
  '35479': { city: 'La Aldea de San Nicolás', lat: 27.9923, lng: -15.7923 },
  '35480': { city: 'Agaete', lat: 28.1000, lng: -15.7000 },
  '35488': { city: 'Agaete', lat: 28.1045, lng: -15.7045 },
  '35489': { city: 'Agaete', lat: 28.1089, lng: -15.7089 },
  '35500': { city: 'Arrecife', lat: 28.9631, lng: -13.5478 },
  '35507': { city: 'Arrecife', lat: 28.9676, lng: -13.5523 },
  '35508': { city: 'Arrecife', lat: 28.9720, lng: -13.5567 },
  '35509': { city: 'Arrecife', lat: 28.9765, lng: -13.5612 },
  '35510': { city: 'Puerto del Carmen', lat: 28.9217, lng: -13.6653 },
  '35518': { city: 'Tías', lat: 28.9489, lng: -13.6556 },
  '35519': { city: 'Tías', lat: 28.9534, lng: -13.6601 },
  '35520': { city: 'Puerto del Carmen', lat: 28.9217, lng: -13.6653 },
  '35530': { city: 'Teguise', lat: 29.0583, lng: -13.5617 },
  '35540': { city: 'Playa Honda', lat: 28.9333, lng: -13.6000 },
  '35550': { city: 'San Bartolomé', lat: 29.0000, lng: -13.6167 },
  '35560': { city: 'Tinajo', lat: 29.0633, lng: -13.6767 },
  '35570': { city: 'Yaiza', lat: 28.9500, lng: -13.7667 },
  '35580': { city: 'Playa Blanca', lat: 28.8611, lng: -13.8339 },
  '35600': { city: 'Puerto del Rosario', lat: 28.5000, lng: -13.8617 },
  '35610': { city: 'Antigua', lat: 28.4167, lng: -14.0167 },
  '35620': { city: 'Gran Tarajal', lat: 28.2167, lng: -14.0333 },
  '35625': { city: 'Morro Jable', lat: 28.0497, lng: -14.3519 },
  '35626': { city: 'Costa Calma', lat: 28.1500, lng: -14.2333 },
  '35627': { city: 'Pájara', lat: 28.3500, lng: -14.1000 },
  '35628': { city: 'Tuineje', lat: 28.3333, lng: -14.0500 },
  '35629': { city: 'Tuineje', lat: 28.3378, lng: -14.0545 },
  '35630': { city: 'La Oliva', lat: 28.6167, lng: -13.9333 },
  '35637': { city: 'Corralejo', lat: 28.7300, lng: -13.8678 },
  '35638': { city: 'El Cotillo', lat: 28.6833, lng: -14.0000 },
  '35639': { city: 'Lajares', lat: 28.6667, lng: -13.9500 },
  '35640': { city: 'La Oliva', lat: 28.6167, lng: -13.9333 },
  '35650': { city: 'Betancuria', lat: 28.4258, lng: -14.0583 },
  
  // MAINLAND SPAIN (main cities)
  '28001': { city: 'Madrid', lat: 40.4168, lng: -3.7038 },
  '28002': { city: 'Madrid', lat: 40.4389, lng: -3.6767 },
  '28003': { city: 'Madrid', lat: 40.4401, lng: -3.7045 },
  '28004': { city: 'Madrid', lat: 40.4256, lng: -3.6978 },
  '28005': { city: 'Madrid', lat: 40.4067, lng: -3.7123 },
  '28006': { city: 'Madrid', lat: 40.4345, lng: -3.6856 },
  '28007': { city: 'Madrid', lat: 40.4178, lng: -3.6712 },
  '28008': { city: 'Madrid', lat: 40.4234, lng: -3.7189 },
  '28009': { city: 'Madrid', lat: 40.4301, lng: -3.6789 },
  '28010': { city: 'Madrid', lat: 40.4456, lng: -3.6934 },
  '08001': { city: 'Barcelona', lat: 41.3874, lng: 2.1686 },
  '08002': { city: 'Barcelona', lat: 41.3834, lng: 2.1767 },
  '08003': { city: 'Barcelona', lat: 41.3801, lng: 2.1856 },
  '08004': { city: 'Barcelona', lat: 41.3712, lng: 2.1623 },
  '08005': { city: 'Barcelona', lat: 41.3945, lng: 2.1989 },
  '08006': { city: 'Barcelona', lat: 41.4023, lng: 2.1534 },
  '08007': { city: 'Barcelona', lat: 41.3912, lng: 2.1667 },
  '08008': { city: 'Barcelona', lat: 41.3889, lng: 2.1545 },
  '08009': { city: 'Barcelona', lat: 41.3956, lng: 2.1712 },
  '08010': { city: 'Barcelona', lat: 41.3867, lng: 2.1734 },
  '46001': { city: 'Valencia', lat: 39.4699, lng: -0.3763 },
  '46002': { city: 'Valencia', lat: 39.4734, lng: -0.3712 },
  '46003': { city: 'Valencia', lat: 39.4678, lng: -0.3801 },
  '46004': { city: 'Valencia', lat: 39.4712, lng: -0.3756 },
  '46005': { city: 'Valencia', lat: 39.4656, lng: -0.3823 },
  '41001': { city: 'Sevilla', lat: 37.3891, lng: -5.9845 },
  '41002': { city: 'Sevilla', lat: 37.3934, lng: -5.9912 },
  '41003': { city: 'Sevilla', lat: 37.3856, lng: -5.9789 },
  '41004': { city: 'Sevilla', lat: 37.3912, lng: -5.9867 },
  '41005': { city: 'Sevilla', lat: 37.3801, lng: -5.9756 },
  '29001': { city: 'Málaga', lat: 36.7213, lng: -4.4214 },
  '29002': { city: 'Málaga', lat: 36.7256, lng: -4.4178 },
  '29003': { city: 'Málaga', lat: 36.7178, lng: -4.4256 },
  '29004': { city: 'Málaga', lat: 36.7234, lng: -4.4145 },
  '29005': { city: 'Málaga', lat: 36.7145, lng: -4.4289 },
  '03001': { city: 'Alicante', lat: 38.3452, lng: -0.4810 },
  '03002': { city: 'Alicante', lat: 38.3489, lng: -0.4756 },
  '03003': { city: 'Alicante', lat: 38.3412, lng: -0.4867 },
  '50001': { city: 'Zaragoza', lat: 41.6488, lng: -0.8891 },
  '50002': { city: 'Zaragoza', lat: 41.6523, lng: -0.8834 },
  '50003': { city: 'Zaragoza', lat: 41.6456, lng: -0.8945 },
  '48001': { city: 'Bilbao', lat: 43.2630, lng: -2.9350 },
  '48002': { city: 'Bilbao', lat: 43.2667, lng: -2.9289 },
  '48003': { city: 'Bilbao', lat: 43.2589, lng: -2.9412 },
  '15001': { city: 'A Coruña', lat: 43.3623, lng: -8.4115 },
  '15002': { city: 'A Coruña', lat: 43.3667, lng: -8.4056 },
  '15003': { city: 'A Coruña', lat: 43.3578, lng: -8.4178 },
  '07001': { city: 'Palma de Mallorca', lat: 39.5696, lng: 2.6502 },
  '07002': { city: 'Palma de Mallorca', lat: 39.5734, lng: 2.6556 },
  '07003': { city: 'Palma de Mallorca', lat: 39.5656, lng: -2.6445 },
  '07004': { city: 'Palma de Mallorca', lat: 39.5712, lng: 2.6489 },
};

// Function to lookup postal code and get geolocation
async function lookupPostalCodeWithGeo(postalCode, country = 'España') {
  // First try local database
  if (SPANISH_POSTAL_CODES[postalCode]) {
    return {
      found: true,
      ...SPANISH_POSTAL_CODES[postalCode],
      source: 'local'
    };
  }
  
  // Try to find nearest postal code as suggestion
  const prefix = postalCode.substring(0, 3);
  const nearestCodes = Object.keys(SPANISH_POSTAL_CODES)
    .filter(code => code.startsWith(prefix))
    .slice(0, 5);
  
  // Try API fallback (Nominatim - free OpenStreetMap geocoding)
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?postalcode=${postalCode}&country=Spain&format=json&limit=1`,
      { headers: { 'User-Agent': 'TantraConnect/1.0' } }
    );
    
    if (response.ok) {
      const data = await response.json();
      if (data && data.length > 0) {
        const result = data[0];
        // Extract city from display_name
        const parts = result.display_name.split(', ');
        const city = parts[0] || parts[1] || 'Unknown';
        return {
          found: true,
          city: city,
          lat: parseFloat(result.lat),
          lng: parseFloat(result.lon),
          source: 'api',
          nearestCodes
        };
      }
    }
  } catch (error) {
    console.error('Geocoding API error:', error);
  }
  
  // Return not found with suggestions
  return {
    found: false,
    city: '',
    lat: 0,
    lng: 0,
    source: 'none',
    nearestCodes
  };
}

// ==================== BECOME TEACHER FORM ====================
export function BecomeTeacherForm() {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuthStore();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  
  const [profileImage, setProfileImage] = useState(null);
  const [bioEn, setBioEn] = useState('');
  const [bioEs, setBioEs] = useState('');
  const [bioIt, setBioIt] = useState('');
  const [specialties, setSpecialties] = useState('');
  const [experience, setExperience] = useState('');
  const [certifications, setCertifications] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [country, setCountry] = useState('España');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [cityAutoFilled, setCityAutoFilled] = useState(false);

  // Auto-fill city when postal code changes
  useEffect(() => {
    if (country === 'España' && postalCode.length === 5) {
      const result = lookupSpanishPostalCode(postalCode);
      if (result.found) {
        setCity(result.city);
        setCityAutoFilled(true);
      }
    } else {
      setCityAutoFilled(false);
    }
  }, [postalCode, country]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!bioEn.trim() || !city.trim() || !country.trim() || !phone.trim() || !postalCode.trim()) {
      alert('Please fill in all required fields (Bio English, Phone, City, Country, Postal Code)');
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/teachers/register`,
        {
          bio: {
            en: bioEn,
            es: bioEs || bioEn,
            it: bioIt || bioEn,
          },
          specialties: specialties.split(',').map(s => s.trim()).filter(Boolean),
          experience_years: parseInt(experience) || 0,
          certifications: certifications.split(',').map(s => s.trim()).filter(Boolean),
          images: profileImage ? [profileImage] : [],
          location: {
            address: '',
            city,
            country,
            postal_code: postalCode,
            latitude: 0,
            longitude: 0,
          },
          phone,
          website: website || null,
        },
        { headers: getAuthHeaders() }
      );
      
      alert('Your teacher profile has been submitted for review. You will be notified once approved.');
      navigate('/profile');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 animate-fade-in" data-testid="become-teacher-page">
      <button onClick={() => navigate(-1)} className="flex items-center text-secondary mb-6 hover:text-gold">
        <ChevronRight className="h-4 w-4 rotate-180 mr-1" /> Back
      </button>
      
      <h1 className="font-serif text-3xl font-bold text-primary mb-6">{t('profile.becomeTeacher')}</h1>
      
      {/* Info Card */}
      <div className="flex gap-3 p-4 rounded-xl mb-6" style={{ backgroundColor: '#F5EFE6' }}>
        <Info className="w-6 h-6 text-gold flex-shrink-0" style={{ color: '#D4A574' }} />
        <p className="text-sm text-secondary">
          Register as a teacher to offer your tantric services. Your profile will be reviewed before activation.
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-6 space-y-6">
        {/* Profile Image */}
        <ImageUpload
          image={profileImage}
          onImageChange={setProfileImage}
          label="Profile Photo"
          aspectRatio={IMAGE_FORMATS.PROFILE.aspectRatio}
          maxSize={IMAGE_FORMATS.PROFILE.maxSize}
          shape="circle"
          placeholder="Add your photo"
        />
        
        {/* Bio Fields */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Bio (English) *</label>
          <Textarea
            placeholder="Tell us about yourself and your practice..."
            value={bioEn}
            onChange={(e) => setBioEn(e.target.value)}
            rows={4}
            required
            data-testid="bio-en"
          />
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Bio (Spanish)</label>
          <Textarea
            placeholder="Cuéntanos sobre ti y tu práctica..."
            value={bioEs}
            onChange={(e) => setBioEs(e.target.value)}
            rows={4}
            data-testid="bio-es"
          />
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Bio (Italian)</label>
          <Textarea
            placeholder="Parlaci di te e della tua pratica..."
            value={bioIt}
            onChange={(e) => setBioIt(e.target.value)}
            rows={4}
            data-testid="bio-it"
          />
        </div>
        
        {/* Specialties & Experience */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Specialties (comma separated)</label>
          <Input
            placeholder="Tantric massage, Meditation, Couple sessions"
            value={specialties}
            onChange={(e) => setSpecialties(e.target.value)}
            data-testid="specialties"
          />
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Certifications (comma separated)</label>
          <Input
            placeholder="ISTA Certified, Tantra Yoga Teacher"
            value={certifications}
            onChange={(e) => setCertifications(e.target.value)}
            data-testid="certifications"
          />
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Years of Experience</label>
          <Input
            type="number"
            placeholder="5"
            value={experience}
            onChange={(e) => setExperience(e.target.value)}
            min={0}
            data-testid="experience"
          />
        </div>
        
        {/* Contact Information */}
        <h3 className="font-semibold text-primary pt-4 border-t">Contact Information</h3>
        
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Phone *</label>
          <Input
            placeholder="+34 612 345 678"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
            data-testid="phone"
          />
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Website (optional)</label>
          <Input
            placeholder="https://yourwebsite.com"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            type="url"
            data-testid="website"
          />
        </div>
        
        {/* Location */}
        <h3 className="font-semibold text-primary pt-4 border-t">Location</h3>
        
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Country *</label>
          <Input
            placeholder="España"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            required
            data-testid="country"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">Postal Code (CAP) *</label>
            <Input
              placeholder="38650"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              maxLength={5}
              required
              data-testid="postal-code"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">
              City * {cityAutoFilled && <span className="text-green-600 text-xs">(auto-filled)</span>}
            </label>
            <Input
              placeholder="Los Cristianos"
              value={city}
              onChange={(e) => { setCity(e.target.value); setCityAutoFilled(false); }}
              required
              className={cityAutoFilled ? 'bg-green-50 border-green-300' : ''}
              data-testid="city"
            />
          </div>
        </div>
        
        <Button 
          type="submit" 
          disabled={isLoading} 
          className="w-full bg-gold hover:bg-gold/90 text-white py-3" 
          style={{ backgroundColor: '#D4A574' }}
          data-testid="submit-teacher"
        >
          {isLoading ? 'Submitting...' : 'Submit Application'}
        </Button>
      </form>
    </div>
  );
}

// ==================== BECOME SCHOOL FORM ====================
export function BecomeSchoolForm() {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuthStore();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  
  const [logoImage, setLogoImage] = useState(null);
  const [nameEn, setNameEn] = useState('');
  const [nameEs, setNameEs] = useState('');
  const [nameIt, setNameIt] = useState('');
  const [descEn, setDescEn] = useState('');
  const [descEs, setDescEs] = useState('');
  const [descIt, setDescIt] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [country, setCountry] = useState('España');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [cityAutoFilled, setCityAutoFilled] = useState(false);

  useEffect(() => {
    if (country === 'España' && postalCode.length === 5) {
      const result = lookupSpanishPostalCode(postalCode);
      if (result.found) {
        setCity(result.city);
        setCityAutoFilled(true);
      }
    } else {
      setCityAutoFilled(false);
    }
  }, [postalCode, country]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!nameEn.trim() || !descEn.trim() || !city.trim() || !country.trim() || !phone.trim() || !postalCode.trim()) {
      alert('Please fill in all required fields');
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/schools/register`,
        {
          name: { en: nameEn, es: nameEs || nameEn, it: nameIt || nameEn },
          description: { en: descEn, es: descEs || descEn, it: descIt || descEn },
          phone,
          website: website || null,
          images: logoImage ? [logoImage] : [],
          location: {
            address: '',
            city,
            country,
            postal_code: postalCode,
            latitude: 0,
            longitude: 0,
          },
        },
        { headers: getAuthHeaders() }
      );
      
      alert('Your school profile has been submitted for review. You will be notified once approved.');
      navigate('/profile');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 animate-fade-in" data-testid="become-school-page">
      <button onClick={() => navigate(-1)} className="flex items-center text-secondary mb-6 hover:text-gold">
        <ChevronRight className="h-4 w-4 rotate-180 mr-1" /> Back
      </button>
      
      <h1 className="font-serif text-3xl font-bold text-primary mb-6">{t('profile.becomeSchool')}</h1>
      
      <div className="flex gap-3 p-4 rounded-xl mb-6" style={{ backgroundColor: '#F5EFE6' }}>
        <Info className="w-6 h-6 text-gold flex-shrink-0" style={{ color: '#D4A574' }} />
        <p className="text-sm text-secondary">
          Register your school to offer tantra courses and workshops. Your school will be reviewed before activation.
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-6 space-y-6">
        {/* School Logo */}
        <ImageUpload
          image={logoImage}
          onImageChange={setLogoImage}
          label="School Logo"
          aspectRatio={IMAGE_FORMATS.PROFILE.aspectRatio}
          maxSize={IMAGE_FORMATS.PROFILE.maxSize}
          shape="circle"
          placeholder="Add school logo"
        />
        
        {/* Name Fields */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">School Name (English) *</label>
          <Input placeholder="Your school name" value={nameEn} onChange={(e) => setNameEn(e.target.value)} required />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">School Name (Spanish)</label>
          <Input placeholder="Nombre de la escuela" value={nameEs} onChange={(e) => setNameEs(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">School Name (Italian)</label>
          <Input placeholder="Nome della scuola" value={nameIt} onChange={(e) => setNameIt(e.target.value)} />
        </div>
        
        {/* Description Fields */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Description (English) *</label>
          <Textarea placeholder="Describe your school and programs..." value={descEn} onChange={(e) => setDescEn(e.target.value)} rows={4} required />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Description (Spanish)</label>
          <Textarea placeholder="Describe tu escuela y programas..." value={descEs} onChange={(e) => setDescEs(e.target.value)} rows={4} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Description (Italian)</label>
          <Textarea placeholder="Descrivi la tua scuola e i programmi..." value={descIt} onChange={(e) => setDescIt(e.target.value)} rows={4} />
        </div>
        
        {/* Contact */}
        <h3 className="font-semibold text-primary pt-4 border-t">Contact Information</h3>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Phone *</label>
          <Input placeholder="+34 612 345 678" value={phone} onChange={(e) => setPhone(e.target.value)} required />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Website (optional)</label>
          <Input placeholder="https://yourschool.com" value={website} onChange={(e) => setWebsite(e.target.value)} type="url" />
        </div>
        
        {/* Location */}
        <h3 className="font-semibold text-primary pt-4 border-t">Location</h3>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Country *</label>
          <Input placeholder="España" value={country} onChange={(e) => setCountry(e.target.value)} required />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">Postal Code (CAP) *</label>
            <Input placeholder="38650" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} maxLength={5} required />
          </div>
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">City * {cityAutoFilled && <span className="text-green-600 text-xs">(auto)</span>}</label>
            <Input placeholder="Los Cristianos" value={city} onChange={(e) => { setCity(e.target.value); setCityAutoFilled(false); }} required className={cityAutoFilled ? 'bg-green-50' : ''} />
          </div>
        </div>
        
        <Button type="submit" disabled={isLoading} className="w-full bg-gold text-white py-3" style={{ backgroundColor: '#D4A574' }}>
          {isLoading ? 'Submitting...' : 'Submit Application'}
        </Button>
      </form>
    </div>
  );
}

// ==================== CREATE SERVICE FORM ====================
export function CreateServiceForm() {
  const { t, i18n } = useTranslation();
  const { getAuthHeaders, user } = useAuthStore();
  const navigate = useNavigate();
  const language = user?.preferred_language || i18n.language || 'es';
  const [isLoading, setIsLoading] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  
  const [serviceImage, setServiceImage] = useState(null);
  const [serviceType, setServiceType] = useState(null);
  const [title, setTitle] = useState({ es: '', en: '', it: '' });
  const [description, setDescription] = useState({ es: '', en: '', it: '' });
  const [price, setPrice] = useState('');
  const [duration, setDuration] = useState('60');
  const [targetAudience, setTargetAudience] = useState([]);
  const [country, setCountry] = useState('España');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [cityAutoFilled, setCityAutoFilled] = useState(false);
  const [coordinates, setCoordinates] = useState({ lat: 0, lng: 0 });
  const [geoError, setGeoError] = useState('');
  const [nearestCodes, setNearestCodes] = useState([]);

  const toggleTargetAudience = (key) => {
    if (key === 'all') {
      setTargetAudience(targetAudience.includes('all') ? [] : ['all']);
    } else {
      let newAudience = targetAudience.filter(a => a !== 'all');
      if (newAudience.includes(key)) {
        newAudience = newAudience.filter(a => a !== key);
      } else {
        newAudience.push(key);
      }
      setTargetAudience(newAudience);
    }
  };

  useEffect(() => {
    const doLookup = async () => {
      if (country === 'España' && postalCode.length === 5) {
        setIsLookingUp(true);
        setGeoError('');
        setNearestCodes([]);
        
        const result = await lookupPostalCodeWithGeo(postalCode, country);
        
        if (result.found) {
          setCity(result.city);
          setCoordinates({ lat: result.lat, lng: result.lng });
          setCityAutoFilled(true);
          setGeoError('');
        } else {
          setCityAutoFilled(false);
          setCoordinates({ lat: 0, lng: 0 });
          setNearestCodes(result.nearestCodes || []);
          setGeoError(language === 'es' 
            ? 'Código postal no encontrado. La geolocalización es obligatoria.'
            : 'Postal code not found. Geolocation is required.');
        }
        setIsLookingUp(false);
      } else {
        setCityAutoFilled(false);
        setGeoError('');
      }
    };
    
    const timeoutId = setTimeout(doLookup, 500);
    return () => clearTimeout(timeoutId);
  }, [postalCode, country, language]);

  const labels = {
    es: {
      back: 'Volver',
      title: 'Crear Servicio',
      servicePhoto: 'Foto del Servicio',
      addPhoto: 'Añade una foto para tu servicio',
      serviceType: 'Tipo de Servicio',
      serviceTitle: 'Título',
      serviceDescription: 'Descripción',
      enterTitle: 'Introduce el título del servicio',
      enterDescription: 'Describe tu servicio en detalle...',
      price: 'Precio (EUR)',
      duration: 'Duración (min)',
      reservedFor: 'Reservado a',
      location: 'Ubicación',
      country: 'País',
      postalCode: 'Código Postal',
      city: 'Ciudad',
      autoFilled: '(auto)',
      geoDetected: 'Geolocalización detectada automáticamente',
      lookingUp: 'Buscando ubicación...',
      nearestCodes: 'Códigos postales cercanos:',
      geoRequired: 'La geolocalización es obligatoria para publicar tu servicio.',
      infoText: 'Tu servicio será revisado por un administrador antes de ser visible para los usuarios.',
      submit: 'Enviar para Aprobación',
      submitting: 'Enviando...',
      selectType: 'Por favor selecciona un tipo de servicio',
      enterTitleAlert: 'Por favor introduce un título',
      enterDescAlert: 'Por favor introduce una descripción',
      enterLocationAlert: 'Por favor introduce el código postal y la ciudad',
      geoRequiredAlert: 'La geolocalización es obligatoria. Por favor introduce un código postal válido.',
      successMsg: 'Tu servicio ha sido enviado para aprobación del administrador.',
      errorMsg: 'Error al crear el servicio',
    },
    en: {
      back: 'Back',
      title: 'Create Service',
      servicePhoto: 'Service Photo',
      addPhoto: 'Add a photo for your service',
      serviceType: 'Service Type',
      serviceTitle: 'Title',
      serviceDescription: 'Description',
      enterTitle: 'Enter service title',
      enterDescription: 'Describe your service in detail...',
      price: 'Price (EUR)',
      duration: 'Duration (min)',
      reservedFor: 'Reserved for',
      location: 'Location',
      country: 'Country',
      postalCode: 'Postal Code',
      city: 'City',
      autoFilled: '(auto)',
      geoDetected: 'Geolocation detected automatically',
      lookingUp: 'Looking up location...',
      nearestCodes: 'Nearest postal codes:',
      geoRequired: 'Geolocation is required to publish your service.',
      infoText: 'Your service will be reviewed by an administrator before it becomes visible to users.',
      submit: 'Submit for Approval',
      submitting: 'Submitting...',
      selectType: 'Please select a service type',
      enterTitleAlert: 'Please enter a title',
      enterDescAlert: 'Please enter a description',
      enterLocationAlert: 'Please enter postal code and city',
      geoRequiredAlert: 'Geolocation is required. Please enter a valid postal code.',
      successMsg: 'Your service has been submitted for admin approval.',
      errorMsg: 'Failed to create service',
    }
  };
  
  const l = labels[language] || labels.es;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!serviceType) { alert(l.selectType); return; }
    if (!title[language]) { alert(l.enterTitleAlert); return; }
    if (!description[language]) { alert(l.enterDescAlert); return; }
    if (!postalCode.trim() || !city.trim()) { alert(l.enterLocationAlert); return; }
    
    // Block submission if no geolocation
    if (coordinates.lat === 0 && coordinates.lng === 0) {
      alert(l.geoRequiredAlert);
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/services`,
        {
          service_type: serviceType,
          title,
          description,
          price: price ? parseFloat(price) : null,
          currency: 'EUR',
          duration_minutes: parseInt(duration) || 60,
          target_audience: targetAudience.length > 0 ? targetAudience : ['all'],
          images: serviceImage ? [serviceImage] : [],
          location: {
            city,
            country,
            address: '',
            postal_code: postalCode,
            latitude: coordinates.lat,
            longitude: coordinates.lng,
          },
        },
        { headers: getAuthHeaders() }
      );
      
      alert(l.successMsg);
      navigate('/profile');
    } catch (error) {
      alert(error.response?.data?.detail || l.errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 animate-fade-in" data-testid="create-service-page">
      <button onClick={() => navigate(-1)} className="flex items-center text-secondary mb-6 hover:text-gold">
        <ChevronRight className="h-4 w-4 rotate-180 mr-1" /> {l.back}
      </button>
      
      <h1 className="font-serif text-3xl font-bold text-primary mb-6">{l.title}</h1>
      
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-6 space-y-6">
        {/* Service Image */}
        <ImageUpload
          image={serviceImage}
          onImageChange={setServiceImage}
          label={l.servicePhoto}
          aspectRatio={IMAGE_FORMATS.SERVICE.aspectRatio}
          maxSize={IMAGE_FORMATS.SERVICE.maxSize}
          shape="rectangle"
          placeholder={l.addPhoto}
          language={language}
        />
        
        {/* Service Type Selection */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-3">{l.serviceType} *</label>
          <div className="grid grid-cols-2 gap-3">
            {SERVICE_TYPES.map((type) => (
              <button
                key={type.key}
                type="button"
                onClick={() => setServiceType(type.key)}
                className={`p-4 rounded-xl border-2 text-center transition-all ${
                  serviceType === type.key 
                    ? 'border-gold bg-gold text-white' 
                    : 'border-gray-200 hover:border-gold'
                }`}
                style={serviceType === type.key ? { backgroundColor: '#D4A574', borderColor: '#D4A574' } : {}}
                data-testid={`service-type-${type.key}`}
              >
                <span className="text-2xl block mb-2">{type.icon}</span>
                <span className="text-sm font-medium">{type.label[language] || type.label.es}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Title */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{l.serviceTitle} ({language.toUpperCase()}) *</label>
          <Input
            placeholder={l.enterTitle}
            value={title[language]}
            onChange={(e) => setTitle({ ...title, [language]: e.target.value })}
            required
            data-testid="service-title"
          />
        </div>
        
        {/* Description */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{l.serviceDescription} ({language.toUpperCase()}) *</label>
          <Textarea
            placeholder={l.enterDescription}
            value={description[language]}
            onChange={(e) => setDescription({ ...description, [language]: e.target.value })}
            rows={4}
            required
            data-testid="service-description"
          />
        </div>
        
        {/* Target Audience */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-3">{l.reservedFor}</label>
          <div className="flex flex-wrap gap-2">
            {TARGET_AUDIENCE.map((audience) => (
              <button
                key={audience.key}
                type="button"
                onClick={() => toggleTargetAudience(audience.key)}
                className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
                  targetAudience.includes(audience.key)
                    ? 'border-gold bg-gold text-white'
                    : 'border-gray-200 text-secondary hover:border-gold'
                }`}
                style={targetAudience.includes(audience.key) ? { backgroundColor: '#D4A574', borderColor: '#D4A574' } : {}}
                data-testid={`audience-${audience.key}`}
              >
                {audience.label[language] || audience.label.es}
              </button>
            ))}
          </div>
        </div>
        
        {/* Price & Duration */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{l.price}</label>
            <Input
              type="number"
              placeholder="0.00"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              min={0}
              data-testid="price"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{l.duration}</label>
            <Input
              type="number"
              placeholder="60"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              min={15}
              data-testid="duration"
            />
          </div>
        </div>
        
        {/* Location */}
        <h3 className="font-semibold text-primary pt-4 border-t">{l.location} *</h3>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{l.country} *</label>
          <Input placeholder="España" value={country} onChange={(e) => setCountry(e.target.value)} required data-testid="country" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{l.postalCode} *</label>
            <Input 
              placeholder="38650" 
              value={postalCode} 
              onChange={(e) => setPostalCode(e.target.value)} 
              maxLength={5} 
              required 
              data-testid="postal-code"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">
              {l.city} * {cityAutoFilled && <span className="text-green-600 text-xs">{l.autoFilled}</span>}
            </label>
            <Input 
              placeholder="Los Cristianos" 
              value={city} 
              onChange={(e) => { setCity(e.target.value); setCityAutoFilled(false); setCoordinates({ lat: 0, lng: 0 }); }} 
              required 
              className={cityAutoFilled ? 'bg-green-50 border-green-300' : geoError ? 'border-red-300' : ''}
              data-testid="city"
            />
          </div>
        </div>
        
        {/* Geolocation Status */}
        {isLookingUp && (
          <p className="text-xs text-blue-600 flex items-center gap-1">
            <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            {l.lookingUp}
          </p>
        )}
        {cityAutoFilled && coordinates.lat !== 0 && (
          <p className="text-xs text-green-600 flex items-center gap-1">
            <MapPin className="h-3 w-3" /> {l.geoDetected}
          </p>
        )}
        {geoError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-xs text-red-600 font-medium">{geoError}</p>
            {nearestCodes.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-red-500">{l.nearestCodes}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {nearestCodes.map(code => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setPostalCode(code)}
                      className="px-2 py-0.5 bg-white border border-red-300 rounded text-xs text-red-600 hover:bg-red-100"
                    >
                      {code} ({SPANISH_POSTAL_CODES[code]?.city})
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Info Box */}
        <div className="flex gap-3 p-4 rounded-xl" style={{ backgroundColor: '#E8F4FD' }}>
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0" />
          <p className="text-sm text-blue-700">{l.infoText}</p>
        </div>
        
        <Button 
          type="submit" 
          disabled={isLoading || (coordinates.lat === 0 && coordinates.lng === 0)} 
          className="w-full bg-gold text-white py-3 disabled:opacity-50 disabled:cursor-not-allowed" 
          style={{ backgroundColor: '#D4A574' }} 
          data-testid="submit-service"
        >
          {isLoading ? l.submitting : l.submit}
        </Button>
      </form>
    </div>
  );
}

// ==================== CREATE COURSE FORM ====================
export function CreateCourseForm() {
  const { t, i18n } = useTranslation();
  const { getAuthHeaders, user } = useAuthStore();
  const navigate = useNavigate();
  const language = user?.preferred_language || i18n.language || 'es';
  const [isLoading, setIsLoading] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  
  const [courseImage, setCourseImage] = useState(null);
  const [courseType, setCourseType] = useState(null);
  const [title, setTitle] = useState({ es: '', en: '', it: '' });
  const [description, setDescription] = useState({ es: '', en: '', it: '' });
  const [price, setPrice] = useState('');
  const [durationHours, setDurationHours] = useState('');
  const [maxParticipants, setMaxParticipants] = useState('');
  const [isOnline, setIsOnline] = useState(false);
  const [isResidential, setIsResidential] = useState(false);
  const [targetAudience, setTargetAudience] = useState([]);
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('España');
  const [postalCode, setPostalCode] = useState('');
  const [cityAutoFilled, setCityAutoFilled] = useState(false);
  const [coordinates, setCoordinates] = useState({ lat: 0, lng: 0 });
  const [geoError, setGeoError] = useState('');
  const [nearestCodes, setNearestCodes] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Auto-fill city and coordinates when postal code changes
  useEffect(() => {
    if (isOnline) return; // Skip lookup for online courses
    
    const doLookup = async () => {
      if (country === 'España' && postalCode.length === 5) {
        setIsLookingUp(true);
        setGeoError('');
        setNearestCodes([]);
        
        const result = await lookupPostalCodeWithGeo(postalCode, country);
        
        if (result.found) {
          setCity(result.city);
          setCoordinates({ lat: result.lat, lng: result.lng });
          setCityAutoFilled(true);
          setGeoError('');
        } else {
          setCityAutoFilled(false);
          setCoordinates({ lat: 0, lng: 0 });
          setNearestCodes(result.nearestCodes || []);
          setGeoError(language === 'es' 
            ? 'Código postal no encontrado. La geolocalización es obligatoria.'
            : 'Postal code not found. Geolocation is required.');
        }
        setIsLookingUp(false);
      } else {
        setCityAutoFilled(false);
        setGeoError('');
      }
    };
    
    const timeoutId = setTimeout(doLookup, 500);
    return () => clearTimeout(timeoutId);
  }, [postalCode, country, language, isOnline]);

  const toggleTargetAudience = (key) => {
    if (key === 'all') {
      setTargetAudience(targetAudience.includes('all') ? [] : ['all']);
    } else {
      let newAudience = targetAudience.filter(a => a !== 'all');
      if (newAudience.includes(key)) {
        newAudience = newAudience.filter(a => a !== key);
      } else {
        newAudience.push(key);
      }
      setTargetAudience(newAudience);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!courseType) { alert(language === 'es' ? 'Por favor selecciona un tipo de curso' : 'Please select a course type'); return; }
    if (!title[language]) { alert(language === 'es' ? 'Por favor introduce un título' : 'Please enter a title'); return; }
    if (!description[language]) { alert(language === 'es' ? 'Por favor introduce una descripción' : 'Please enter a description'); return; }
    if (!isOnline && (!postalCode.trim() || !city.trim())) {
      alert(language === 'es' ? 'Por favor introduce el código postal y la ciudad' : 'Please enter postal code and city');
      return;
    }
    
    // Block submission if no geolocation (unless online)
    if (!isOnline && coordinates.lat === 0 && coordinates.lng === 0) {
      alert(language === 'es' 
        ? 'La geolocalización es obligatoria. Por favor introduce un código postal válido.'
        : 'Geolocation is required. Please enter a valid postal code.');
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/courses`,
        {
          course_type: courseType,
          title,
          description,
          price: price ? parseFloat(price) : null,
          currency: 'EUR',
          duration_hours: parseInt(durationHours) || 0,
          max_participants: parseInt(maxParticipants) || null,
          is_online: isOnline,
          is_residential: isResidential,
          target_audience: targetAudience.length > 0 ? targetAudience : ['all'],
          start_date: startDate || null,
          end_date: endDate || null,
          images: courseImage ? [courseImage] : [],
          location: isOnline ? { city: 'Online', country: '', address: '', postal_code: '', latitude: 0, longitude: 0 } : {
            city,
            country,
            address: '',
            postal_code: postalCode,
            latitude: coordinates.lat,
            longitude: coordinates.lng,
          },
        },
        { headers: getAuthHeaders() }
      );
      
      alert(language === 'es' 
        ? 'Tu curso ha sido enviado para aprobación del administrador. Se te notificará cuando sea aprobado.'
        : 'Your course has been submitted for admin approval. You will be notified once it\'s approved.');
      navigate('/profile');
    } catch (error) {
      alert(error.response?.data?.detail || (language === 'es' ? 'Error al crear el curso' : 'Failed to create course'));
    } finally {
      setIsLoading(false);
    }
  };

  const labels = {
    es: {
      back: 'Volver',
      title: 'Crear Curso',
      coursePhoto: 'Foto del Curso',
      addPhoto: 'Añade una foto para tu curso',
      courseType: 'Tipo de Curso',
      courseTitle: 'Título',
      courseDescription: 'Descripción',
      enterTitle: 'Introduce el título del curso',
      enterDescription: 'Describe tu curso en detalle...',
      onlineCourse: 'Curso Online',
      residentialCourse: 'Curso Residencial',
      residentialInfo: 'Incluye alojamiento y comidas',
      courseFor: 'Curso Para',
      startDate: 'Fecha de Inicio',
      endDate: 'Fecha de Fin',
      price: 'Precio (EUR)',
      duration: 'Duración (horas)',
      maxParticipants: 'Máx. Participantes',
      location: 'Ubicación',
      postalCode: 'Código Postal',
      city: 'Ciudad',
      country: 'País',
      autoFilled: '(auto)',
      infoText: 'Tu curso será revisado por un administrador antes de ser visible para los usuarios.',
      submit: 'Enviar para Aprobación',
      submitting: 'Enviando...',
    },
    en: {
      back: 'Back',
      title: 'Create Course',
      coursePhoto: 'Course Photo',
      addPhoto: 'Add a photo for your course',
      courseType: 'Course Type',
      courseTitle: 'Title',
      courseDescription: 'Description',
      enterTitle: 'Enter course title',
      enterDescription: 'Describe your course in detail...',
      onlineCourse: 'Online Course',
      residentialCourse: 'Residential Course',
      residentialInfo: 'Includes accommodation and meals',
      courseFor: 'Course For',
      startDate: 'Start Date',
      endDate: 'End Date',
      price: 'Price (EUR)',
      duration: 'Duration (hours)',
      maxParticipants: 'Max Participants',
      location: 'Location',
      postalCode: 'Postal Code',
      city: 'City',
      country: 'Country',
      autoFilled: '(auto)',
      infoText: 'Your course will be reviewed by an administrator before it becomes visible to users.',
      submit: 'Submit for Approval',
      submitting: 'Submitting...',
    }
  };
  
  const l = labels[language] || labels.es;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 animate-fade-in" data-testid="create-course-page">
      <button onClick={() => navigate(-1)} className="flex items-center text-secondary mb-6 hover:text-gold">
        <ChevronRight className="h-4 w-4 rotate-180 mr-1" /> {l.back}
      </button>
      
      <h1 className="font-serif text-3xl font-bold text-primary mb-6">{l.title}</h1>
      
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-6 space-y-6">
        {/* Course Image */}
        <ImageUpload
          image={courseImage}
          onImageChange={setCourseImage}
          label={l.coursePhoto}
          aspectRatio={IMAGE_FORMATS.COURSE.aspectRatio}
          maxSize={IMAGE_FORMATS.COURSE.maxSize}
          shape="rectangle"
          placeholder={l.addPhoto}
          language={language}
        />
        
        {/* Course Type Selection */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-3">{l.courseType} *</label>
          <div className="grid grid-cols-2 gap-4">
            {COURSE_TYPES.map((type) => (
              <button
                key={type.key}
                type="button"
                onClick={() => setCourseType(type.key)}
                className={`p-5 rounded-xl border-2 text-center transition-all ${
                  courseType === type.key 
                    ? 'border-gold bg-gold text-white' 
                    : 'border-gray-200 hover:border-gold'
                }`}
                style={courseType === type.key ? { backgroundColor: '#D4A574', borderColor: '#D4A574' } : {}}
                data-testid={`course-type-${type.key}`}
              >
                <type.icon className={`w-8 h-8 mx-auto mb-2 ${courseType === type.key ? 'text-white' : 'text-gold'}`} style={courseType !== type.key ? { color: '#D4A574' } : {}} />
                <span className="font-semibold block">{type.label[language] || type.label.es}</span>
                <span className={`text-xs ${courseType === type.key ? 'text-white/80' : 'text-secondary'}`}>{type.description[language] || type.description.es}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Title & Description */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{l.courseTitle} ({language.toUpperCase()}) *</label>
          <Input placeholder={l.enterTitle} value={title[language]} onChange={(e) => setTitle({ ...title, [language]: e.target.value })} required data-testid="course-title" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{l.courseDescription} ({language.toUpperCase()}) *</label>
          <Textarea placeholder={l.enterDescription} value={description[language]} onChange={(e) => setDescription({ ...description, [language]: e.target.value })} rows={4} required data-testid="course-description" />
        </div>
        
        {/* Online Toggle */}
        <div 
          className="flex items-center justify-between p-4 bg-cream rounded-xl cursor-pointer"
          style={{ backgroundColor: '#F8F6F3' }}
          onClick={() => setIsOnline(!isOnline)}
          data-testid="online-toggle"
        >
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-gold" style={{ color: '#D4A574' }} />
            <span className="font-medium">{l.onlineCourse}</span>
          </div>
          <div className={`w-12 h-7 rounded-full p-1 transition-colors ${isOnline ? 'bg-gold' : 'bg-gray-300'}`} style={isOnline ? { backgroundColor: '#D4A574' } : {}}>
            <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${isOnline ? 'translate-x-5' : ''}`} />
          </div>
        </div>
        
        {/* Residential Toggle (only if not online) */}
        {!isOnline && (
          <div 
            className="flex items-center justify-between p-4 bg-cream rounded-xl cursor-pointer"
            style={{ backgroundColor: '#F8F6F3' }}
            onClick={() => setIsResidential(!isResidential)}
            data-testid="residential-toggle"
          >
            <div className="flex items-center gap-3">
              <Leaf className="w-5 h-5 text-gold" style={{ color: '#D4A574' }} />
              <div>
                <span className="font-medium block">{l.residentialCourse}</span>
                <span className="text-xs text-secondary">{l.residentialInfo}</span>
              </div>
            </div>
            <div className={`w-12 h-7 rounded-full p-1 transition-colors ${isResidential ? 'bg-gold' : 'bg-gray-300'}`} style={isResidential ? { backgroundColor: '#D4A574' } : {}}>
              <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${isResidential ? 'translate-x-5' : ''}`} />
            </div>
          </div>
        )}
        
        {/* Target Audience */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-3">{l.courseFor}</label>
          <div className="flex flex-wrap gap-2">
            {TARGET_AUDIENCE.map((audience) => (
              <button
                key={audience.key}
                type="button"
                onClick={() => toggleTargetAudience(audience.key)}
                className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
                  targetAudience.includes(audience.key)
                    ? 'border-gold bg-gold text-white'
                    : 'border-gray-200 text-secondary hover:border-gold'
                }`}
                style={targetAudience.includes(audience.key) ? { backgroundColor: '#D4A574', borderColor: '#D4A574' } : {}}
                data-testid={`audience-${audience.key}`}
              >
                {audience.label[language] || audience.label.es}
              </button>
            ))}
          </div>
        </div>
        
        {/* Dates */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{l.startDate}</label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="start-date" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{l.endDate}</label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="end-date" />
          </div>
        </div>
        
        {/* Price, Duration, Participants */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{l.price}</label>
            <Input type="number" placeholder="0.00" value={price} onChange={(e) => setPrice(e.target.value)} min={0} data-testid="price" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{l.duration}</label>
            <Input type="number" placeholder="0" value={durationHours} onChange={(e) => setDurationHours(e.target.value)} min={0} data-testid="duration" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{l.maxParticipants}</label>
            <Input type="number" placeholder="∞" value={maxParticipants} onChange={(e) => setMaxParticipants(e.target.value)} min={1} data-testid="max-participants" />
          </div>
        </div>
        
        {/* Location (if not online) */}
        {!isOnline && (
          <>
            <h3 className="font-semibold text-primary pt-4 border-t">{l.location}</h3>
            <div>
              <label className="block text-sm font-semibold text-primary mb-2">{l.country} *</label>
              <Input placeholder="España" value={country} onChange={(e) => setCountry(e.target.value)} required data-testid="country" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-primary mb-2">{l.postalCode} *</label>
                <Input 
                  placeholder="38650" 
                  value={postalCode} 
                  onChange={(e) => setPostalCode(e.target.value)} 
                  maxLength={5} 
                  required 
                  data-testid="postal-code"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-primary mb-2">
                  {l.city} * {cityAutoFilled && <span className="text-green-600 text-xs">{l.autoFilled}</span>}
                </label>
                <Input 
                  placeholder="Los Cristianos" 
                  value={city} 
                  onChange={(e) => { setCity(e.target.value); setCityAutoFilled(false); setCoordinates({ lat: 0, lng: 0 }); }} 
                  required 
                  className={cityAutoFilled ? 'bg-green-50 border-green-300' : geoError ? 'border-red-300' : ''}
                  data-testid="city"
                />
              </div>
            </div>
            
            {/* Geolocation Status */}
            {isLookingUp && (
              <p className="text-xs text-blue-600 flex items-center gap-1">
                <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                {language === 'es' ? 'Buscando ubicación...' : 'Looking up location...'}
              </p>
            )}
            {cityAutoFilled && coordinates.lat !== 0 && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <MapPin className="h-3 w-3" /> 
                {language === 'es' ? 'Geolocalización detectada automáticamente' : 'Geolocation detected automatically'}
              </p>
            )}
            {geoError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs text-red-600 font-medium">{geoError}</p>
                {nearestCodes.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-red-500">{language === 'es' ? 'Códigos postales cercanos:' : 'Nearest postal codes:'}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {nearestCodes.map(code => (
                        <button
                          key={code}
                          type="button"
                          onClick={() => setPostalCode(code)}
                          className="px-2 py-0.5 bg-white border border-red-300 rounded text-xs text-red-600 hover:bg-red-100"
                        >
                          {code} ({SPANISH_POSTAL_CODES[code]?.city})
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        
        {/* Info Box */}
        <div className="flex gap-3 p-4 rounded-xl" style={{ backgroundColor: '#E8F4FD' }}>
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0" />
          <p className="text-sm text-blue-700">{l.infoText}</p>
        </div>
        
        <Button 
          type="submit" 
          disabled={isLoading || (!isOnline && coordinates.lat === 0 && coordinates.lng === 0)} 
          className="w-full bg-gold text-white py-3 disabled:opacity-50 disabled:cursor-not-allowed" 
          style={{ backgroundColor: '#D4A574' }} 
          data-testid="submit-course"
        >
          {isLoading ? l.submitting : l.submit}
        </Button>
      </form>
    </div>
  );
}

export default { BecomeTeacherForm, BecomeSchoolForm, CreateServiceForm, CreateCourseForm };
